/**
 * 学生側メッセージ受信箱ページ
 * スレッドベースのメッセージ管理画面（学生用）
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Block as BlockIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useMessageThreads } from '../../hooks/useMessageThreads';
import { ThreadStatus, ReplyType } from '../../types/message-thread';
import { MainLayout } from '../../components/Layout/MainLayout';
import { ThreadList } from '../../components/MessageThread/ThreadList';
import { ThreadConversation } from '../../components/MessageThread/ThreadConversation';
import { ThreadFilters } from '../../components/MessageThread/ThreadFilters';

export const MessageInboxPage: React.FC = () => {
  const [showFilters, setShowFilters] = useState(false);

  // レスポンシブ対応
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showConversation, setShowConversation] = useState(false);

  const initialQuery = useMemo(
    () => ({
      limit: 20,
      sortBy: 'lastMessageAt' as const,
      sortOrder: 'DESC' as const,
    }),
    []
  );

  const {
    threads,
    currentThread,
    uiState,
    pagination,
    loadThreads,
    loadThreadDetail,
    sendMessage,
    updateThreadStatus,
    updateFilters,
    updateSort,
    loadNextPage,
    loadPrevPage,
    hasNextPage,
    hasPrevPage,
    totalUnread,
  } = useMessageThreads({
    userType: 'student',
    autoLoad: true,
    initialQuery,
  });

  // スレッド選択
  const handleSelectThread = async (threadId: string) => {
    await loadThreadDetail(threadId);

    // モバイルでは会話画面に切り替え
    if (isMobile) {
      setShowConversation(true);
    }
  };

  // 戻るボタン処理（モバイル用）
  const handleBackToList = () => {
    setShowConversation(false);
  };

  // メッセージ送信（学生は返信タイプを指定可能）
  const handleSendMessage = async (
    messageBody: string,
    replyType?: ReplyType
  ) => {
    if (!uiState.selectedThreadId) return;

    await sendMessage(uiState.selectedThreadId, {
      messageBody,
      replyType,
    });
  };

  // スレッド操作
  const handleArchiveThread = async (threadId: string) => {
    await updateThreadStatus(threadId, ThreadStatus.ARCHIVED);
  };

  const handleBlockThread = async (threadId: string) => {
    await updateThreadStatus(threadId, ThreadStatus.BLOCKED);
  };

  const handleActivateThread = async (threadId: string) => {
    await updateThreadStatus(threadId, ThreadStatus.ACTIVE);
  };

  // リフレッシュ
  const handleRefresh = () => {
    loadThreads();
  };

  // フィルター状態に応じた統計表示ラベルを取得
  const getStatsLabel = () => {
    if (uiState.filters.onlyArchived) {
      return 'アーカイブ済み';
    }
    if (uiState.filters.onlyBlocked) {
      return 'ブロック済み';
    }
    if (uiState.filters.includeArchived && uiState.filters.includeBlocked) {
      return '総スレッド数';
    }
    if (uiState.filters.includeArchived) {
      return '総スレッド数';
    }
    return 'アクティブ';
  };

  return (
    <MainLayout showFooter={false}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h4" component="h1">
              スカウトメッセージ
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="更新">
                <IconButton
                  onClick={handleRefresh}
                  disabled={uiState.isLoading}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* 統計情報 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              label={`未読: ${totalUnread}件`}
              color={totalUnread > 0 ? 'error' : 'default'}
              variant={totalUnread > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              label={`${getStatsLabel()}: ${pagination.total}件`}
              variant="outlined"
            />
          </Box>

          {/* エラー表示 */}
          {uiState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uiState.error}
            </Alert>
          )}
        </Box>

        {/* メインコンテンツ */}
        {isMobile ? (
          /* モバイル版: スライドパネル方式 */
          <Paper
            sx={{
              height: 'calc(100vh - 160px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {showConversation ? (
              /* 会話画面 */
              <>
                {/* 戻るボタン付きヘッダー */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <IconButton onClick={handleBackToList} size="small">
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6">メッセージ</Typography>
                </Box>

                {/* 会話エリア */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {uiState.selectedThreadId ? (
                    <StudentThreadConversation
                      threadDetail={currentThread}
                      loading={uiState.isLoadingDetail}
                      sending={uiState.isSending}
                      onSendMessage={handleSendMessage}
                      canReply={currentThread?.canReply ?? true}
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body1">
                        スレッドを選択してメッセージを表示
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              /* スレッド一覧 */
              <>
                {/* フィルター */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <ThreadFilters
                    filters={uiState.filters}
                    onFiltersChange={updateFilters}
                    userType="student"
                  />
                </Box>

                {/* スレッドリスト */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <ThreadList
                    threads={threads}
                    selectedThreadId={uiState.selectedThreadId}
                    onSelectThread={handleSelectThread}
                    loading={uiState.isLoading}
                    userType="student"
                    onArchive={handleArchiveThread}
                    onBlock={handleBlockThread}
                    onActivate={handleActivateThread}
                    pagination={pagination}
                    onNextPage={loadNextPage}
                    onPrevPage={loadPrevPage}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                  />
                </Box>
              </>
            )}
          </Paper>
        ) : (
          /* PC版: 従来の左右分割 */
          <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
            {/* スレッド一覧 */}
            <Grid item md={4}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* フィルター */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <ThreadFilters
                    filters={uiState.filters}
                    onFiltersChange={updateFilters}
                    userType="student"
                  />
                </Box>

                {/* スレッドリスト */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <ThreadList
                    threads={threads}
                    selectedThreadId={uiState.selectedThreadId}
                    onSelectThread={handleSelectThread}
                    loading={uiState.isLoading}
                    userType="student"
                    onArchive={handleArchiveThread}
                    onBlock={handleBlockThread}
                    onActivate={handleActivateThread}
                    pagination={pagination}
                    onNextPage={loadNextPage}
                    onPrevPage={loadPrevPage}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* 会話エリア */}
            <Grid item md={8}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {uiState.selectedThreadId ? (
                  <StudentThreadConversation
                    threadDetail={currentThread}
                    loading={uiState.isLoadingDetail}
                    sending={uiState.isSending}
                    onSendMessage={handleSendMessage}
                    canReply={currentThread?.canReply ?? true}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body1">
                      スレッドを選択してメッセージを表示
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </MainLayout>
  );
};

// 学生用の会話コンポーネント（返信タイプ選択機能付き）
const StudentThreadConversation: React.FC<{
  threadDetail: any;
  loading: boolean;
  sending: boolean;
  onSendMessage: (messageBody: string, replyType?: ReplyType) => Promise<void>;
  canReply: boolean;
}> = ({ threadDetail, loading, sending, onSendMessage, canReply }) => {
  const [selectedReplyType, setSelectedReplyType] = useState<ReplyType>(
    ReplyType.OTHER
  );

  const handleSendMessage = async (messageBody: string) => {
    await onSendMessage(messageBody, selectedReplyType);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ThreadConversation
        threadDetail={threadDetail}
        loading={loading}
        sending={sending}
        onSendMessage={handleSendMessage}
        userType="student"
        canReply={canReply}
      />

      {/* 返信タイプ選択 */}
      {canReply && threadDetail?.thread.status === ThreadStatus.ACTIVE && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'white',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}
          >
            返信タイプを選択してください
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.values(ReplyType).map((type) => (
              <Chip
                key={type}
                label={getReplyTypeLabel(type)}
                variant={selectedReplyType === type ? 'filled' : 'outlined'}
                color={selectedReplyType === type ? 'primary' : 'default'}
                size="small"
                onClick={() => setSelectedReplyType(type)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// 返信タイプのラベル取得
const getReplyTypeLabel = (type: ReplyType): string => {
  switch (type) {
    case ReplyType.POSITIVE:
      return '興味あり';
    case ReplyType.NEGATIVE:
      return '興味なし';
    case ReplyType.QUESTION:
      return '質問';
    case ReplyType.OTHER:
      return 'その他';
    default:
      return type;
  }
};
